import { mobx } from '@/lib/mobx';
import { getValidGoogleFontsUrl } from '@/helpers/getValidGoogleFontsUrl';

/**
 * Fetches and imports a Google Font into the document or Shadow DOM.
 * It validates the Google Font URL, fetches the font CSS, parses its `@font-face` rules,
 * and applies the rules as separate stylesheets specific to font families.
 *
 * @param googleFontUrl - The URL pointing to the Google Font to be fetched.
 * @param isInstallFont - A flag indicating whether the font should be added globally.
 * @returns A promise resolving to a 'font-family' name or `false` in case of any error.
 */
export const importGoogleFont = async ({
  googleFontUrl,
  isInstallFont,
}: ImportGoogleFontProps): Promise<string | false> => {
  // Check if the browser supports the CSSStyleSheet API and adoptedStyleSheets
  if (!('adoptedStyleSheets' in document)) {
    console.warn('The browser does not support CSSStyleSheet or adoptedStyleSheets.');
    return false;
  }

  try {
    const validUrl = getValidGoogleFontsUrl(googleFontUrl);
    if (!validUrl) {
      console.warn('Invalid Google Font URL was provided.');
      return false;
    }

    const response = await fetch(validUrl);
    if (!response.ok) {
      console.warn('Failed to fetch Google Font:', response.statusText);
      return false;
    }

    const font = await response.text();
    const cssSheets: CSSStyleSheet[] = [...document.adoptedStyleSheets];
    const styleSheet: FontCSSStyleSheet = new CSSStyleSheet();
    const { cssRules } = await styleSheet.replace(font);

    let currentFontName = '';
    const sheetId = isInstallFont ? 'fontFaceInitial' : 'fontFaceId';
    for (const rule of cssRules) {
      if (rule instanceof CSSFontFaceRule) {
        currentFontName = clearFontName(rule.style.getPropertyValue('font-family'));
        break;
      }
    }

    const fontSheet = cssSheets.find(
      (sheet: FontCSSStyleSheet) => sheet[sheetId] === currentFontName,
    );

    if (fontSheet) {
      await fontSheet.replace(font);
    } else {
      styleSheet[sheetId] = currentFontName;
      document.adoptedStyleSheets = [...document.adoptedStyleSheets, styleSheet];
    }

    // If the font should be globally applied, create a new stylesheet and apply it to the app's root Shadow DOM.
    if (isInstallFont) {
      const styleSheetInitial = [...mobx.ui.appRoot.adoptedStyleSheets].find(
        (sheet: FontCSSStyleSheet) => sheet.styleSheetId === 'set-font-family',
      );

      const styleSheetInitialFont: FontCSSStyleSheet = styleSheetInitial ?? new CSSStyleSheet();

      await styleSheetInitialFont.replace(
        `#app, #app-container {
          font-family: '${currentFontName}';
        }
        input, textarea, select, button {
          font-family: '${currentFontName}';
        }`,
      );

      styleSheetInitialFont['styleSheetId'] = 'set-font-family';
      styleSheetInitialFont['defaultFontUrl'] = validUrl;

      if (!styleSheetInitial) {
        mobx.ui.appRoot.adoptedStyleSheets = [
          ...mobx.ui.appRoot.adoptedStyleSheets,
          styleSheetInitialFont,
        ];
      }
    }

    return currentFontName;
  } catch (error) {
    console.warn('Error importing Google Font:', error);
    return false;
  }
};

const clearFontName = (fontName: string): string => {
  return (
    fontName
      .split(',')[0]
      ?.replace(/[\\'`"]/g, '')
      .trim() ?? ''
  );
};

interface ImportGoogleFontProps {
  googleFontUrl: string;
  isInstallFont?: boolean;
}

export interface FontCSSStyleSheet extends CSSStyleSheet {
  fontFaceId?: string;
  styleSheetId?: string;
  fontFaceInitial?: string;
  defaultFontUrl?: string;
}
